import { NextPage } from 'next';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { DESKTOP_MEDIA } from '@common/constants/MEDIAS';
import categoryMenu from '@data/categoryMenu.json';
import { css } from '@emotion/react';
import { HeaderV2Options } from '@type/web/header';

import { palette } from '@lib/styles/palette';

import AppLayout from '@components/web/Layout/AppLayout';
import CategoryProducts from '@components/web/category/CategoryProducts';

export type CategoryDataType = {
  seq: number;
  name: string;
  imageUrl: string;
  parentSeq: number;
  level: number;
  auth: number;
  path: string;
  categoryFilter: Array<{ categoryDepth: number; categorySeq: number }>;
  children?: any;
  hasChildren?: boolean;
};

const Category: NextPage = ({
  categoryDataList,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const pageTitle = '카테고리 목록';
  const headerOptions: HeaderV2Options = {
    isFixed: {
      pc: true,
      tablet: true,
    },
    isTopPosition: false,
    isShowBackground: true,
    showAppDownloadBtn: false,
    showNavigationBar: ['mobile', 'tablet', 'desktop'],
  };
  const Breadcrumb = () => {
    return (
      <div css={breadcrumbStyles}>
        <Link href="/">
          <a>홈</a>
        </Link>
        <span>&gt;</span>
        <span>카테고리</span>
      </div>
    );
  };

  return (
    <AppLayout headerOptions={headerOptions}>
      <NextSeo
        title="카테고리 목록"
        description={`중고나라 전체 카테고리 목록입니다: 수입명품, 패션의류, 패션잡화, 뷰티, 출산/유아동, 모바일/태블릿, 가전제품, 노트북/PC, 카메라/캠코더, 가구/인테리어, 리빙/생황, 게임, 반려동물/취미, 도서/음반/문구, 티켓/쿠폰, 스포츠, 레저/여행, 중고차, 오토바이, 공구/산업용품, 무료나눔, NFT 까지 원하는 상품에 맞는 카테고리를 선택하세요.`}
        canonical={`${process.env.NEXT_PUBLIC_DEFAULT_URL}${router.asPath}`}
        openGraph={{
          type: 'website',
          locale: 'ko_KR',
          url: `${process.env.NEXT_PUBLIC_DEFAULT_URL}${router.asPath}`,
          site_name: '중고나라',
          title: '중고나라 카테고리 목록',
          description: '중고나라 전체 카테고리 목록입니다.',
        }}
      />
      <div css={wrapper}>
        <Breadcrumb />
        <h1 css={titleStyles}>{pageTitle}</h1>
        {categoryDataList.map((categoryData: CategoryDataType) => {
          return (
            <CategoryProducts
              categoryData={categoryData}
              key={categoryData.seq}
              parentCategoryTitle={pageTitle}
            />
          );
        })}
      </div>
    </AppLayout>
  );
};

export default Category;

const wrapper = css`
  width: 100%;
  margin: 0 auto;
  max-width: 1280px;
  padding: 128px 0 44px 0;
  display: flex;
  flex-direction: column;
  ${DESKTOP_MEDIA} {
    padding: 160px 0 44px 0;
  }
`;
const titleStyles = css`
  font-size: 24px;
  font-weight: 600;
  line-height: 34px;
  padding: 20px;
`;

const breadcrumbStyles = css`
  display: flex;
  padding: 0 20px 0 20px;
  font-size: 12px;
  a,
  span {
    display: inline-block;
    padding: 0 7px 0 0;
    margin-right: 7px;
    text-decoration: none;
    color: ${palette.gray6};
  }
  a:first-child {
    padding-left: 0;
  }
`;

export const getStaticProps: GetStaticProps = async () => {
  const firstDepthCategoryList: Array<CategoryDataType> = [];
  categoryMenu.forEach((category): any => {
    if (category.level === 1) {
      firstDepthCategoryList.push(category);
    }
  });
  return { props: { categoryDataList: firstDepthCategoryList } };
};
